// Centralized metadata for all pages
export interface PageMeta {
  title: string;
  description: string;
  image?: string;
}

export type HreflangLink = {
  href: string;
  hreflang: string;
};

export const BASE_URL = 'https://pixelpro.solutions';

// Service pages metadata
export const serviceMeta: Record<string, PageMeta> = {
  'web-design': {
    title: 'Κατασκευή Ιστοσελίδων | PixelPro Solutions',
    description: 'Δημιουργούμε σύγχρονες, responsive ιστοσελίδες που ξεχωρίζουν. WordPress, custom development, e-commerce λύσεις με έμφαση στο UX/UI design.',
    image: '/og/web-design.jpg'
  },
  'ecommerce': {
    title: 'Κατασκευή E-Shop | PixelPro Solutions',
    description: 'Δημιουργούμε e-shop που πουλάνε: WooCommerce, custom δυναμικές λειτουργίες, SEO & ταχύτητα. Από απλά καταστήματα έως πολυσύνθετες πλατφόρμες.',
    image: '/og/ecommerce.jpg'
  },
  'branding': {
    title: 'Branding & Ταυτότητα | PixelPro Solutions',
    description: 'Δημιουργούμε ισχυρές μάρκες που αφήνουν εντύπωση. Logo design, brand guidelines, packaging και στρατηγική τοποθέτησης στην αγορά.',
    image: '/og/branding.jpg'
  },
  'marketing-strategy': {
    title: 'Στρατηγική Marketing | PixelPro Solutions',
    description: 'Αναπτύσσουμε στρατηγικές marketing που φέρνουν αποτελέσματα. Digital marketing, content strategy, social media και performance analysis.',
    image: '/og/marketing-strategy.jpg'
  },
  'seo': {
    title: 'SEO Υπηρεσίες | PixelPro Solutions',
    description: 'Τεχνικό SEO, on-page βελτιστοποίηση και στρατηγική περιεχομένου για περισσότερη οργανική επισκεψιμότητα, καλύτερες κατατάξεις και qualified leads.',
    image: '/og/seo.jpg'
  },
  'apps': {
    title: 'Εφαρμογές Web & Mobile | PixelPro Solutions',
    description: 'Αναπτύσσουμε εφαρμογές για iOS και Android με σύγχρονες τεχνολογίες. React Native, Flutter και native development για κάθε ανάγκη.',
    image: '/og/mobile-app.jpg'
  },
  'content-production': {
    title: 'Παραγωγή Περιεχομένου | PixelPro Solutions',
    description: 'Δημιουργούμε περιεχόμενο που εμπνέει και πείθει. Blog posts, social media content, video scripts και visual storytelling.',
    image: '/og/content-production.jpg'
  }
};

export const serviceMetaEn: Record<string, PageMeta> = {
  'web-design': {
    title: 'Web Design & Development | PixelPro Solutions',
    description:
      'Modern, responsive websites built for speed, SEO, and conversions. WordPress or custom builds, UX/UI design, analytics, and ongoing support.',
    image: '/og/web-design.jpg'
  },
  'ecommerce': {
    title: 'E-commerce Development | PixelPro Solutions',
    description:
      'High-converting online stores with WooCommerce or custom builds: performance, SEO, payments, automation, and a clean checkout experience that scales.',
    image: '/og/ecommerce.jpg'
  },
  'branding': {
    title: 'Branding & Identity | PixelPro Solutions',
    description:
      'A clear brand system: logo, typography, color, messaging, and guidelines so your business stays consistent across web, social, and print.',
    image: '/og/branding.jpg'
  },
  'marketing-strategy': {
    title: 'Marketing Strategy | PixelPro Solutions',
    description:
      'A practical growth plan across SEO, paid ads, content, email, and social built on tracking, experimentation, and clear KPIs.',
    image: '/og/marketing-strategy.jpg'
  },
  'seo': {
    title: 'SEO Services | PixelPro Solutions',
    description:
      'Technical SEO, on-page optimization, and content strategy to increase rankings, traffic, and qualified leads with audits, fixes, and reporting.',
    image: '/og/seo.jpg'
  },
  'apps': {
    title: 'Mobile & Web Apps | PixelPro Solutions',
    description:
      'Mobile and web app development with modern stacks (React Native, Flutter, or custom): UX, performance, and reliable maintenance.',
    image: '/og/mobile-app.jpg'
  },
  'content-production': {
    title: 'Content Production | PixelPro Solutions',
    description:
      'Content that converts: copywriting, landing pages, articles, social posts, scripts, and creative assets aligned to your brand and SEO.',
    image: '/og/content-production.jpg'
  }
};

// Homepage metadata
export const homeMeta: PageMeta = {
  title: 'PixelPro Solutions - Ψηφιακές Λύσεις Τοπικών Επιχειρήσεων',
  description: 'Web development, e-commerce, branding και digital marketing για τοπικές επιχειρήσεις. Χτίζουμε γρήγορα sites, SEO και στρατηγικές που φέρνουν leads.',
  image: '/og-image.jpg'
};

export const homeMetaEn: PageMeta = {
  title: 'PixelPro Solutions – Digital Solutions for Local Businesses',
  description:
    'Digital solutions for local businesses: web development, e-commerce, branding, and marketing built for speed, SEO, and measurable growth.',
  image: '/og-image.jpg'
};

// Other pages metadata
export const pagesMeta: Record<string, PageMeta> = {
  'about': {
    title: 'Σχετικά με Εμάς – PixelPro Solutions Team & Vision',
    description: 'Μάθετε για την ομάδα μας, την φιλοσοφία και την δέσμευσή μας να παρέχουμε εξαιρετικές ψηφιακές λύσεις για κάθε επιχείρηση.'
  },
  'services': {
    title: 'Υπηρεσίες – Web, SEO, Marketing | PixelPro Solutions',
    description: 'Ανακαλύψτε υπηρεσίες web design, e-commerce, apps, branding, SEO και digital marketing — με στόχο περισσότερα leads και πωλήσεις.'
  },
  'portfolio': {
    title: 'Portfolio – Έργα & Case Studies | PixelPro Solutions',
    description: 'Δείτε επιλεγμένα έργα και case studies της PixelPro Solutions: ιστοσελίδες, e‑shops, branding και marketing. Πραγματικά αποτελέσματα με μετρήσιμα KPIs.'
  },
  'consultation': {
    title: 'Δωρεάν Συμβουλευτική – Κλείστε Κλήση | PixelPro Solutions',
    description: 'Κλείστε δωρεάν συμβουλευτική 20’ για να αξιολογήσουμε την online παρουσία σας, να ορίσουμε στόχους και να προτείνουμε πρακτικά βήματα ανάπτυξης.'
  },
  'package-selection': {
    title: 'Σύγκριση Πακέτων – PixelPro Solutions',
    description: 'Συγκρίνετε πακέτα υπηρεσιών, παραδοτέα και χρονοδιάγραμμα. Επιλέξτε τη λύση που ταιριάζει στο budget και τους στόχους της επιχείρησής σας.'
  },
  'marketing-cost': {
    title: 'Υπολογιστής Κόστους Marketing – PixelPro Solutions',
    description: 'Υπολογίστε ενδεικτικό budget marketing και πιθανό ROI με βάση στόχους και κανάλια. Ιδανικό για να ξεκινήσετε με ξεκάθαρο πλάνο δράσης.'
  },
  'privacy-policy': {
    title: 'Πολιτική Απορρήτου | PixelPro Solutions',
    description: 'Δείτε ποια δεδομένα συλλέγουμε, πώς τα χρησιμοποιούμε και πώς τα προστατεύουμε. Μάθετε τα δικαιώματά σας και πώς μπορείτε να επικοινωνήσετε μαζί μας.'
  },
  'cookie-policy': {
    title: 'Πολιτική Cookies | PixelPro Solutions',
    description: 'Μάθετε τι cookies χρησιμοποιούμε, για ποιο λόγο και πώς μπορείτε να τα διαχειριστείτε. Στόχος μας είναι καλύτερη εμπειρία, χωρίς υπερβολές.'
  },
  'tetris': {
    title: 'Πρόκληση Tetris | PixelPro Solutions',
    description: 'Παίξτε το παιχνίδι Tetris και κάντε ένα σύντομο διάλειμμα.'
  },
  'not-found': {
    title: 'Η σελίδα δεν βρέθηκε | PixelPro Solutions',
    description: 'Η σελίδα που αναζητάτε δεν υπάρχει ή έχει μετακινηθεί.'
  },
  'contact': {
    title: 'Επικοινωνία – Δωρεάν Συμβουλευτική | PixelPro Solutions',
    description: 'Επικοινωνήστε μαζί μας για μια δωρεάν συμβουλευτική συνεδρία 20 λεπτών. Τηλεφωνήστε μας ή στείλτε μας μήνυμα.'
  },
  // Archive pages
  'posts': {
    title: 'Άρθρα – Ενημερωτικό Περιεχόμενο | PixelPro Solutions',
    description: 'Τελευταία νέα, insights και ενημερώσεις από την ομάδα μας.'
  },
  'knowledge': {
    title: 'Γνώση – Τεχνικοί Πόροι | PixelPro Solutions',
    description: 'Εις βάθος γνώσεις και τεχνικοί πόροι για επαγγελματίες.'
  },
  'guides': {
    title: 'Οδηγοί – Βήμα-βήμα Οδηγίες | PixelPro Solutions',
    description: 'Οδηγοί βήμα-βήμα για να σας βοηθήσουν να πλοηγηθείτε σε πολύπλοκα θέματα.'
  },
  'tools': {
    title: 'Εργαλεία – Χρήσιμα Utilities | PixelPro Solutions',
    description: 'Χρήσιμα εργαλεία και βοηθήματα για να βελτιστοποιήσετε τη ροή εργασίας σας.'
  },
  'legislation': {
    title: 'Νομοθεσία & Κανονισμοί | PixelPro Solutions',
    description: 'Σημαντική νομοθεσία, κανονισμοί και νομικά πλαίσια.'
  },
  'organizations': {
    title: 'Φορείς – Οργανισμοί & Ιδρύματα | PixelPro Solutions',
    description: 'Βασικοί φορείς, ιδρύματα και διοικητικά όργανα.'
  },
  'resources': {
    title: 'Χρήσιμο Υλικό – Downloads & Templates | PixelPro Solutions',
    description: 'Πόροι προς λήψη, πρότυπα και υλικό αναφοράς.'
  },
  'faqs': {
    title: 'Συχνές Ερωτήσεις – FAQ | PixelPro Solutions',
    description: 'Βρείτε απαντήσεις στις πιο συχνές ερωτήσεις.'
  }
};

export const pagesMetaEn: Record<string, PageMeta> = {
  'about': {
    title: 'About Us – PixelPro Solutions Team & Vision',
    description: 'Meet our team, how we work, and what we value: strategy, design, and engineering focused on measurable growth for local businesses.'
  },
  'services': {
    title: 'Our Services – Full Digital Solutions | PixelPro Solutions',
    description: 'Explore our services: web design & development, e-commerce, mobile apps, branding, SEO, and digital marketing—built to drive leads and sales.'
  },
  'portfolio': {
    title: 'Portfolio – Our Work & Case Studies | PixelPro Solutions',
    description: 'See selected projects and case studies: websites, e-commerce, branding, and marketing work with real outcomes and measurable KPIs.'
  },
  'consultation': {
    title: 'Free Consultation – Book a Call | PixelPro Solutions',
    description: 'Book a free 20-minute consultation to review your online presence, set clear goals, and get practical next steps for growth.'
  },
  'package-selection': {
    title: 'Package Comparison – PixelPro Solutions',
    description: 'Compare service packages, deliverables, and timelines to pick the right fit for your budget and goals—then book a free consultation with a clear plan.'
  },
  'marketing-cost': {
    title: 'Marketing Cost Calculator – PixelPro Solutions',
    description: 'Estimate marketing budget and potential ROI based on goals and channels—use it to plan campaigns with clarity before you launch.'
  },
  'privacy-policy': {
    title: 'Privacy Policy | PixelPro Solutions',
    description: 'Learn what data we collect, how we use it, and how we protect it. Review your rights and how to contact us about privacy.'
  },
  'cookie-policy': {
    title: 'Cookie Policy | PixelPro Solutions',
    description: 'See what cookies we use, why we use them, and how you can manage your preferences—so you stay in control of your experience.'
  },
  'tetris': {
    title: 'Tetris Challenge | PixelPro Solutions',
    description: 'Play a quick round of Tetris and take a short break.'
  },
  'not-found': {
    title: 'Page not found | PixelPro Solutions',
    description: 'The page you are looking for does not exist or has moved.'
  },
  'contact': {
    title: 'Contact – Free Consultation | PixelPro Solutions',
    description: 'Get in touch for a free consultation. Call us or send a message to discuss your needs.'
  },
  // Archive pages
  'posts': {
    title: 'Articles – Informative Content | PixelPro Solutions',
    description: 'Latest news, insights, and updates from our team.'
  },
  'knowledge': {
    title: 'Knowledge Base – Technical Resources | PixelPro Solutions',
    description: 'In-depth knowledge and technical resources for professionals.'
  },
  'guides': {
    title: 'Guides – Step-by-Step Instructions | PixelPro Solutions',
    description: 'Step-by-step guides to help you navigate complex topics.'
  },
  'tools': {
    title: 'Tools – Useful Utilities | PixelPro Solutions',
    description: 'Useful tools and utilities to streamline your workflow.'
  },
  'legislation': {
    title: 'Legislation & Regulations | PixelPro Solutions',
    description: 'Important legislation, regulations, and legal frameworks.'
  },
  'organizations': {
    title: 'Organizations – Institutions & Bodies | PixelPro Solutions',
    description: 'Key organizations, institutions, and governing bodies.'
  },
  'resources': {
    title: 'Resources – Downloads & Templates | PixelPro Solutions',
    description: 'Downloadable resources, templates, and reference materials.'
  },
  'faqs': {
    title: 'Frequently Asked Questions – FAQ | PixelPro Solutions',
    description: 'Find answers to the most common questions.'
  }
};

// Helper function to get metadata for a service
export function getServiceMeta(slug: string, lang: 'el' | 'en' = 'el'): PageMeta {
  const source = lang === 'en' ? serviceMetaEn : serviceMeta;
  return source[slug] || {
    title: lang === 'en' ? 'Service | PixelPro Solutions' : 'Υπηρεσία | PixelPro Solutions',
    description:
      lang === 'en'
        ? 'Professional digital services for your business.'
        : 'Επαγγελματικές ψηφιακές υπηρεσίες για την επιχείρησή σας.'
  };
}

// Helper function to get metadata for any page
export function getPageMeta(page: string, slug?: string, lang: 'el' | 'en' = 'el'): PageMeta {
  if (slug) {
    return getServiceMeta(slug, lang);
  }

  const source = lang === 'en' ? pagesMetaEn : pagesMeta;
  return source[page] || (lang === 'en' ? homeMetaEn : homeMeta);
}

// Generate hreflang entries for bilingual content
export function getHreflangLinks(currentPath: string, currentLang: 'el' | 'en' = 'el'): HreflangLink[] {
  const normalizedPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const isEnglishPath = normalizedPath === '/en' || normalizedPath.startsWith('/en/');
  const elPath = isEnglishPath ? normalizedPath.replace(/^\/en/, '') || '/' : normalizedPath;
  const enPath = isEnglishPath
    ? normalizedPath
    : normalizedPath === '/'
      ? '/en'
      : `/en${normalizedPath}`;

  const links: HreflangLink[] = [
    { href: `${BASE_URL}${elPath}`, hreflang: 'el' },
    { href: `${BASE_URL}${enPath}`, hreflang: 'en' },
    {
      href: `${BASE_URL}${currentLang === 'en' ? enPath : elPath}`,
      hreflang: 'x-default'
    },
  ];

  return links;
}

// Backwards-compatible helper (string output)
export function generateHreflang(currentPath: string, currentLang: 'el' | 'en' = 'el') {
  return getHreflangLinks(currentPath, currentLang)
    .map((link) => `<link rel="alternate" href="${link.href}" hreflang="${link.hreflang}" />`)
    .join('\n');
}
